<template>
    <v-container class="pa-5" fluid>
        <ChangeStatusDialog
            :is-open="showChangeStatusDialog"
            :status-from="statusFrom"
            :status-to="statusTo"
            @on-success="setToImproveStatus"
            @on-close="closeChangeStatusDialog"
        />
        <AddRubberDialog :is-open="showAddRubberDialog" @on-success="getRubbers" @on-close="closeAddRubberDialog" />
        <EditAdditionalJobNumbersDialog
            :is-open="showEditAdditionalJobNumbersDialog"
            :rubber="rubberToEditAdditionalJobNumbers"
            @on-save="handleAdditionalJobNumbersSave"
            @on-close="closeEditAdditionalJobNumbersDialog"
        />
        <CommentsDialog
            :is-open="showCommentsDialog"
            :rubber="rubberForComments"
            @on-close="closeCommentsDialog"
        />

        <page-title>Rejestr gum na lakier dyspersyjny</page-title>
        <v-row align="end">
            <v-col cols="12" sm="4" lg="4">
                <v-btn color="secondary" medium data-sentry-label="Dodaj gumę" @click="openAddRubberDialog">
                    <v-icon left>
                        mdi-plus
                    </v-icon>
                    Dodaj gumę
                </v-btn>
            </v-col>
            <v-col cols="12" sm="8" lg="8">
                <v-container fluid class="pa-0">
                    <v-row justify="end" dense>
                        <v-col cols="12" md="6">
                            <v-select
                                v-model="filters.status_id"
                                label="Status gumy"
                                :items="statuses"
                                item-value="id"
                                item-text="name"
                                no-data-text="Nie znaleziono statusu"
                                solo
                                clearable
                                hide-details
                                @change="changeFilter"
                                @click:clear="clearFilter('status_id')"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="filters.mainJobNumber"
                                append-icon="mdi-magnify"
                                label="Numer główny zlecenia"
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('mainJobNumber')"
                            />
                        </v-col>
                    </v-row>
                    <v-row justify="end" dense>
                        <v-col cols="12" md="2">
                            <v-text-field
                                v-model="filters.serialNumber"
                                append-icon="mdi-magnify"
                                label="L.p."
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('serialNumber')"
                            />
                        </v-col>
                        <v-col cols="12" md="5">
                            <v-text-field
                                v-model="filters.customerName"
                                append-icon="mdi-magnify"
                                label="Klient"
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('customerName')"
                            />
                        </v-col>
                        <v-col cols="12" md="5">
                            <v-text-field
                                v-model="filters.additionalJobNumber"
                                append-icon="mdi-magnify"
                                label="Pozostałe numery zleceń"
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('additionalJobNumber')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
        <v-row justify="end" no-gutters>
            <v-col cols="auto">
                <v-btn
                    text
                    x-small
                    data-sentry-label="Generuj raport"
                    :disabled="rubbersReportLoading || rubbersLoading"
                    @click="generateRubbersReport"
                >
                    <v-icon v-if="!rubbersReportLoading" small color="green">
                        mdi-file-excel
                    </v-icon>
                    <v-progress-circular
                        v-else
                        indeterminate
                        color="primary"
                        class="mr-1"
                        size="12"
                        width="2"
                    />
                    Generuj raport
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :items="rubbers"
                        :loading="rubbersLoading"
                        :mobile-breakpoint="0"
                        :sort-by="'serialNumber'"
                        :sort-desc="false"
                        :items-per-page="5"
                        :page.sync="page"
                    >
                        <template #item.customerName="{item}">
                            {{ item.customer.name }}
                        </template>
                        <template #item.mainJobNumber="{item}">
                            {{ formatJobNumber(item.mainJobNumber, item.impositionNumber) }}
                        </template>
                        <template #item.jobName="{item}">
                            <v-tooltip v-if="parseEmptyValue(item.jobName).length > 15" :open-delay="tooltipDelay" top>
                                <template #activator="{on}">
                                    <span v-on="on">{{ getShortStringOfJobName(item.jobName) }}</span>
                                </template>
                                {{ item.jobName }}
                            </v-tooltip>
                            <span v-else>{{ parseEmptyValue(item.jobName) }}</span>
                        </template>
                        <template #item.lastUsageDateTime="{item}">
                            {{ formatDate(item.lastUsageDateTime, false) }}
                        </template>
                        <template #item.additionalJobNumbers="{item}">
                            {{ formatJobNumber(getFirstJob(item.additionalJobNumbers).name, getFirstJob(item.additionalJobNumbers).impositionNumber) }}

                            <v-tooltip v-if="item.additionalJobNumbers.length > 1" :open-delay="tooltipDelay" bottom>
                                <template #activator="{on}">
                                    <v-chip small class="ml-1" color="warning" v-on="on">
                                        +{{ item.additionalJobNumbers.length - 1 }}
                                    </v-chip>
                                </template>
                                <div
                                    v-for="(additionalJobObject, index) in getWithoutFirstJobNumber(item.additionalJobNumbers)"
                                    :key="index"
                                >
                                    {{ formatJobNumber(additionalJobObject.name, additionalJobObject.impositionNumber) }}
                                </div>
                            </v-tooltip>
                        </template>
                        <template #item.status="{item}">
                            <v-chip :color="getStatusColor(item.statusId)" text-color="black" small>
                                {{ item.status.name }}
                            </v-chip>
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            text
                                            data-sentry-label="Dodaj komentarz"
                                            v-on="on"
                                            @click="openCommentsDialog(item)"
                                        >
                                            <v-badge
                                                :value="item.commentCounter > 0"
                                                :content="item.commentCounter"
                                                color="primary"
                                                overlap
                                            >
                                                <v-icon color="success">
                                                    mdi-comment-plus
                                                </v-icon>
                                            </v-badge>
                                        </v-btn>
                                    </template>
                                    <span>Dodaj komentarz</span>
                                </v-tooltip>
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            text
                                            data-sentry-label="Dodaj pozostałe numery"
                                            v-on="on"
                                            @click="openEditAdditionalJobNumbersDialog(item)"
                                        >
                                            <v-icon color="primary">
                                                mdi-pencil
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Dodaj pozostałe numery</span>
                                </v-tooltip>
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            text
                                            data-sentry-label="Zmień status"
                                            :disabled="!isItemStatusForUse(item)"
                                            v-on="on"
                                            @click="openChangeStatusDialog(item)"
                                        >
                                            <v-icon>
                                                mdi-arrange-send-to-back
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Zmień status</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { errorHandler } from '@/utils/errorHandler'
import dateFormatter from '@/mixins/dateFormatter'
import PageTitle from '@/components/common/PageTitle'
import parse from '@/mixins/parse'
import tooltipDelay from '@/mixins/tooltip'
import ChangeStatusDialog from './ChangeStatusDialog.vue'
import filters from '@/mixins/dataTableFilters'
import AddRubberDialog from './AddRubberDialog.vue'
import EditAdditionalJobNumbersDialog from './EditAdditionalJobNumbersDialog.vue'
import CommentsDialog from './CommentsDialog.vue'

export default {
    name: 'ConfigurationsRubberRegistry',
    components: {
        PageTitle,
        ChangeStatusDialog,
        AddRubberDialog,
        EditAdditionalJobNumbersDialog,
        CommentsDialog
    },
    mixins: [
        dateFormatter,
        parse,
        tooltipDelay,
        filters,
    ],
    data() {
        return {
            filters: {
                status_id: null,
                serialNumber: null,
                customerName: null,
                mainJobNumber: null,
                additionalJobNumber: null
            },
            showChangeStatusDialog: false,
            showAddRubberDialog: false,
            statusFrom: null,
            statusTo: null,
            page: 1,
            headers: [
                {
                    text: 'Lp.',
                    value: 'serialNumber',
                    sortable: true,
                },
                {
                    text: 'Klient',
                    value: 'customerName',
                    sortable: false
                },
                {
                    text: 'Główny nr zlecenia',
                    value: 'mainJobNumber',
                    sortable: false
                },
                {
                    text: 'Nazwa zlecenia',
                    value: 'jobName',
                    sortable: false
                },
                {
                    text: 'Data ostatniego użycia',
                    value: 'lastUsageDateTime',
                    align: 'center',
                    sortable: true
                },
                {
                    text: 'Ilość dni od ostatniego użycia',
                    value: 'daysFromLastUsage',
                    align: 'center',
                    sortable: true
                },
                {
                    text: 'Status',
                    value: 'status',
                    align: 'center',
                    sortable: false
                },
                {
                    text: 'Pozostałe numery zleceń',
                    value: 'additionalJobNumbers',
                    align: 'center',
                    sortable: false
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false
                },
            ],
            rubberIdToStatusChange: null,
            showEditAdditionalJobNumbersDialog: false,
            rubberToEditAdditionalJobNumbers: null,
            showCommentsDialog: false,
            rubberForComments: null
        }
    },
    computed: {
        userEmail() {
            return this.$store.state.user.userEmail
        },
        rubbers() {
            return this.$store.state.configurations.rubberRegistry.rubbers
        },
        statuses() {
            return this.$store.state.configurations.rubberRegistry.statuses
        },
        statusColors() {
            return this.$store.state.configurations.rubberRegistry.statusColors
        },
        rubbersLoading() {
            return this.$store.state.configurations.rubberRegistry.rubbersLoading
        },
        rubbersReportLoading() {
            return this.$store.state.configurations.rubberRegistry.rubbersReportLoading
        },
        filterQuery() {
            const filters = Object.assign({}, this.filters)
            let query = ''

            for (let item in filters) {
                if (filters[item] !== null && typeof filters[item] !== 'undefined') {
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        }
    },
    mounted() {
        this.getRubbers()
        this.$store.dispatch('configurations/getRubberStatuses')
            .catch(error => {
                errorHandler(error)
            })
    },
    methods: {
        formatJobNumber(jobNumber, impositionNumber) {
            if (!jobNumber) return ''

            return jobNumber && impositionNumber ? `${jobNumber} (m${impositionNumber})` : jobNumber
        },
        getRubbers() {
            this.$store.dispatch('configurations/getRubbers', {
                paginate: '?',
                query: this.filterQuery,
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        generateRubbersReport() {
            this.$store.dispatch('configurations/generateRubbersReport', {
                paginate: '?',
                query: this.filterQuery,
                userEmail: this.userEmail
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        setToImproveStatus() {
            this.$store.dispatch('configurations/setRubberStatus', {
                rubberId: this.rubberIdToStatusChange,
                statusId: this.statusTo.id
            })
                .then(() => {
                    this.getRubbers()
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        getFirstJob(jobNumbers) {
            if (!jobNumbers.length) {
                return '-'
            }

            return jobNumbers[0]
        },
        getWithoutFirstJobNumber(jobNumbers) {
            if (!jobNumbers.length) {
                return []
            }

            return jobNumbers.slice(1)
        },
        getShortStringOfJobName(jobName) {
            if (jobName.length > 15) {
                return jobName.substring(0, 15) + '...'
            }

            return jobName
        },
        getStatusColor(statusId) {
            const statusColor = this.statusColors.find(color => color.statusId === statusId)

            if (statusColor) {
                return statusColor.color
            }

            return 'grey'
        },
        isItemStatusForUse(item) {
            return item.statusId === 10 // status 'Do użycia'
        },
        openChangeStatusDialog(item) {
            this.rubberIdToStatusChange = item.id
            this.statusFrom = this.statuses.find(status => status.id === item.statusId)
            this.statusTo = this.statuses.find(status => status.id === 20) // status 'Do poprawy'
            this.showChangeStatusDialog = true
        },
        closeChangeStatusDialog() {
            this.rubberIdToStatusChange = null
            this.statusFrom = null
            this.statusTo = null
            this.showChangeStatusDialog = false
        },
        openAddRubberDialog() {
            this.showAddRubberDialog = true
        },
        closeAddRubberDialog() {
            this.showAddRubberDialog = false
        },
        openEditAdditionalJobNumbersDialog(item) {
            this.rubberToEditAdditionalJobNumbers = item
            this.showEditAdditionalJobNumbersDialog = true
        },
        closeEditAdditionalJobNumbersDialog() {
            this.showEditAdditionalJobNumbersDialog = false
            this.rubberToEditAdditionalJobNumbers = null
        },
        changeFilter() {
            this.page = 1
            this.getRubbers()
        },
        async handleAdditionalJobNumbersSave(payload) {
            try {
                await this.$store.dispatch('configurations/saveAdditionalJobNumbers', payload)
                this.closeEditAdditionalJobNumbersDialog()
                await this.getRubbers()
            } catch (error) {
                () => { }
            }
        },
        openCommentsDialog(item) {
            this.rubberForComments = item
            this.showCommentsDialog = true
        },
        closeCommentsDialog() {
            this.showCommentsDialog = false
            this.rubberForComments = null
            this.getRubbers()
        }
    },
}
</script>
